// Shared payload/validation logic for the Create and Edit schedule drawers.
// Both drawers read the batch scope out of createBatchFormData (that is what the
// Sites/Brands/Customer autocompletes write to) and the recurrence out of
// scheduleFormData.

export const DATE_RANGE_OPTIONS = [
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_n_days', label: 'Since last run (cadence + 1 day)' },
    { value: 'last_7_days', label: 'Last 7 days' },
    { value: 'last_30_days', label: 'Last 30 days' },
    { value: 'current_month', label: 'Current month' },
    { value: 'previous_month', label: 'Previous month' },
    { value: 'custom', label: 'Custom (fixed dates)' }
]

export const FREQUENCY_OPTIONS = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'custom', label: 'Every N days' }
]

export const BATCH_TYPE_OPTIONS = [
    { value: 'sku2raw', label: 'SKU to Product' },
    { value: 'raw2sku', label: 'Product to SKU' },
    { value: 'raw2raw', label: 'Product to Product' },
    { value: 'war2war', label: 'Competitor to Own' }
]

// The customer lives in its own column; these directions are rejected without it.
export const CUSTOMER_REQUIRED_TYPES = ['sku2raw', 'raw2raw', 'war2war']

export function isScheduleFormValid(data, batchData) {
    if (!data.schedule_name || !data.batch_type || !data.frequency) return false
    if (data.frequency === 'custom' && !(parseInt(data.every_n_days, 10) >= 1)) return false
    if (CUSTOMER_REQUIRED_TYPES.includes(data.batch_type) && !batchData?.customerID) return false
    return true
}

// The scheduler forwards custom dates to ia-normalizer untouched
// (`date_range_type == 'custom'` deliberately skips _calculate_date_range), and
// mapper_batch rejects anything that is not 8 digits: "date_start must be in YYYYMMDD
// format", 400. _execute_schedule raises on that, so update_execution_timestamps never
// runs and next_execution is never advanced — the schedule re-fails on EVERY tick,
// forever, producing no batch and no trace beyond the 207 body of check-and-execute.
// The date fields are <input type="date">, i.e. 'YYYY-MM-DD'; the manual create path
// strips the dashes and this one did not.
function toYyyymmdd(value) {
    if (!value) return value
    return String(value).replace(/-/g, '')
}

export function buildScheduleConfig(data, batchData) {
    const config = {
        sites: batchData.sites || [],
        brands: batchData.brands || []
    }

    if (data.batch_type === 'war2war') {
        // war2war has no date range: its window is "listings first seen in the last N
        // days", resolved in ia-normalizer.
        //
        // ownSites is read from the SCHEDULE's own config, like firstDateDays beside it.
        // It used to come from `batchData` (the create-batch form state), which no
        // schedule drawer populates, renders or clears — so it was unsettable, it leaked
        // whatever a half-open Create Batch drawer had left there for another customer,
        // and editing a schedule to change only its frequency silently saved `[]`. An
        // empty ownSites makes _resolve_scope fan the batch out over EVERY site in
        // RETAILER_PRODUCT, multiplying the task count with nothing in the UI to show it.
        config.ownSites = data.config.ownSites || []
        config.firstDateDays = data.config.firstDateDays || 30
    }

    if (data.batch_type === 'raw2raw') {
        // The novelties scope, on the OWN side: only products first seen in the last N
        // days are anchored. No default, unlike war2war — a raw2raw schedule without it
        // re-anchors the WHOLE own catalogue on every run, which is what the first retail
        // schedules did on 2026-09-23 (Primor: 67.341 own products instead of the 1.198
        // new that week).
        const days = parseInt(data.config.firstDateDays, 10)
        if (days >= 1) config.firstDateDays = days
    }

    if (data.batch_type === 'raw2sku') {
        config.date_range_type = data.config.date_range_type || 'last_30_days'
        config.dateField = batchData.dateField || 'end_date'

        // Only include dateStart/dateEnd if custom
        if (config.date_range_type === 'custom') {
            config.dateStart = toYyyymmdd(batchData.dateStart)
            config.dateEnd = toYyyymmdd(batchData.dateEnd)
        }
    }

    // Left out when empty so the scheduler applies the house default instead.
    const maxDays = parseInt(data.config.maxDaysSinceSeen, 10)
    if (maxDays) config.maxDaysSinceSeen = maxDays

    const asp = data.config.autoSecondPass
    if (asp?.enabled) {
        config.autoSecondPass = {
            execution_mode: asp.speedMode ? 'sync' : 'batch',
            sample_rate: (parseFloat(asp.samplePct) || 0) / 100
        }
    }

    // Add auto-validation thresholds if any were customized
    const av = data.config.autoValidation || {}
    const customizedAv = {}
    if (av.minRateAutovalidate !== null && av.minRateAutovalidate !== '') customizedAv.minRateAutovalidate = parseFloat(av.minRateAutovalidate)
    if (av.minGapSecondCandidate !== null && av.minGapSecondCandidate !== '') customizedAv.minGapSecondCandidate = parseFloat(av.minGapSecondCandidate)
    if (av.maxRateAutoexclude !== null && av.maxRateAutoexclude !== '') customizedAv.maxRateAutoexclude = parseFloat(av.maxRateAutoexclude)
    if (Object.keys(customizedAv).length > 0) config.autoValidation = customizedAv

    return config
}

export function buildSchedulePayload(data, batchData) {
    return {
        schedule_name: data.schedule_name,
        batch_type: data.batch_type,
        id_customer: CUSTOMER_REQUIRED_TYPES.includes(data.batch_type)
            ? (batchData.customerID?.value ?? batchData.customerID)
            : null,
        frequency: data.frequency,
        every_n_days: data.frequency === 'custom' ? parseInt(data.every_n_days, 10) : null,
        // Empty is not "midnight": it means "no hour of my own", and the back fills in
        // the house default. Sending '' would be rejected as an invalid HH:MM.
        execution_time: data.execution_time || null,
        is_active: data.is_active,
        config: buildScheduleConfig(data, batchData)
    }
}
